import React, { useState } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import { Link } from 'react-router-dom'
import { wrapProjects } from '../data/projects'

const Home: React.FC = () => {
  const [openSection, setOpenSection] = useState<string | null>(null)
  const [selectedImage, setSelectedImage] = useState<string | null>(null)

  const capabilities = [
    {
      title: 'Vehicle Wraps',
      description: 'Turn your fleet into mobile billboards.',
      details: 'Professional fleet graphics designed for maximum visibility and brand impact on the road.',
    },
    {
      title: 'Brand Identity',
      description: 'Complete brand systems that define who you are.',
      details: 'From logo design to full brand guidelines, we create cohesive identities that resonate with your audience.',
    },
    {
      title: 'Web Design',
      description: 'Websites and digital experiences that convert.',
      details: 'Custom digital experiences built for performance, conversions, and exceptional user experience.',
    },
  ]

  const toggleSection = (title: string) => {
    if (openSection === title) {
      setOpenSection(null)
    } else {
      setOpenSection(title)
    }
  }

  return (
    <div className="min-h-screen bg-background text-primary selection:bg-accent selection:text-white">
      {/* Hero Section with Video Background */}
      <section className="relative min-h-screen flex items-center justify-center px-6 overflow-hidden">
        {/* Video Background */}
        <div className="absolute inset-0 w-full h-full">
          <video
            autoPlay
            loop
            muted
            playsInline
            className="w-full h-full object-cover opacity-30"
          >
            <source src="/AdobeStock_610235599.mov" type="video/mp4" />
          </video>
          {/* Gradient Overlay */}
          <div className="absolute inset-0 bg-gradient-to-b from-background/80 via-background/50 to-background" />
          <div className="absolute inset-0 bg-[url('/noise.png')] opacity-20 mix-blend-overlay"></div>
        </div>

        {/* Content */}
        <div className="relative z-10 max-w-5xl mx-auto text-center">
          <motion.div
            className="mb-12"
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 1, ease: [0.16, 1, 0.3, 1] }}
          >
            <h1 className="sr-only">CHIPAHUAK</h1>
            <img
              src="/logo-full.png"
              alt="CHIPAHUAK"
              className="h-24 md:h-32 lg:h-40 w-auto mx-auto drop-shadow-2xl brightness-0 invert"
            />
          </motion.div>

          <motion.h2
            className="text-2xl md:text-4xl lg:text-5xl font-display font-medium text-white mb-8 leading-tight tracking-tight"
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 1, delay: 0.2, ease: [0.16, 1, 0.3, 1] }}
          >
            Creative solutions house <br className="hidden md:block" />
            <span className="text-neutral-400">exclusively for businesses.</span>
          </motion.h2>

          <motion.div
            className="flex flex-col sm:flex-row gap-6 justify-center items-center"
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 1, delay: 0.4, ease: [0.16, 1, 0.3, 1] }}
          >
            <a
              href="#services"
              className="group relative px-8 py-4 bg-white text-black text-sm font-bold uppercase tracking-wider rounded-none hover:bg-neutral-200 transition-all duration-300 overflow-hidden"
            >
              <span className="relative z-10">View Solutions</span>
              <div className="absolute inset-0 bg-accent transform scale-x-0 group-hover:scale-x-100 transition-transform origin-left duration-300"></div>
              <span className="absolute inset-0 z-10 flex items-center justify-center text-white opacity-0 group-hover:opacity-100 transition-opacity duration-300">View Solutions</span>
            </a>
            <Link
              to="/portfolio"
              className="group px-8 py-4 border border-white/20 text-white text-sm font-bold uppercase tracking-wider hover:bg-white/10 transition-all duration-300 backdrop-blur-sm"
            >
              See Results
            </Link>
          </motion.div>
        </div>
      </section>

      {/* Featured Case Study */}
      <section className="py-32 px-6">
        <div className="max-w-7xl mx-auto">
          <motion.div
            className="flex flex-col md:flex-row justify-between items-end mb-16 gap-8"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, ease: [0.16, 1, 0.3, 1] }}
            viewport={{ once: true }}
          >
            <div>
              <div className="text-accent font-mono text-sm mb-4 tracking-wider uppercase">Featured Case Study</div>
              <h2 className="text-4xl md:text-6xl font-display font-medium text-white leading-none">
                Driving <span className="text-neutral-500">Growth.</span>
              </h2>
            </div>
            <p className="text-lg text-neutral-400 font-light max-w-md text-right md:text-left">
              See how we delivered measurable results for a logistics company through strategic rebranding and digital transformation.
            </p>
          </motion.div>

          <Link to="/case-studies/smoove-move">
            <motion.div
              className="group relative bg-surfaceHighlight rounded-none overflow-hidden border border-white/5 hover:border-accent/50 transition-colors duration-500"
              initial={{ opacity: 0, y: 40 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 1, ease: [0.16, 1, 0.3, 1] }}
              viewport={{ once: true }}
            >
              <div className="grid md:grid-cols-2 gap-0">
                <div className="p-12 md:p-20 flex flex-col justify-center relative z-10">
                  <h3 className="text-4xl md:text-5xl font-display font-medium mb-6 text-white group-hover:text-accent transition-colors duration-300">
                    Smoove Move Logistics
                  </h3>
                  <p className="text-neutral-400 font-light mb-12 leading-relaxed text-lg">
                    Landing page rebuild, logo rebrand, and complete brand identity—transforming their brand presence into a modern, conversion-focused platform.
                  </p>

                  <div className="grid grid-cols-3 gap-8 mb-12 border-t border-white/10 pt-8">
                    <div>
                      <div className="text-3xl font-display font-medium mb-1 text-white">81%</div>
                      <div className="text-xs text-neutral-500 uppercase tracking-wider">Faster Load</div>
                    </div>
                    <div>
                      <div className="text-3xl font-display font-medium mb-1 text-white">98</div>
                      <div className="text-xs text-neutral-500 uppercase tracking-wider">Mobile Score</div>
                    </div>
                    <div>
                      <div className="text-3xl font-display font-medium mb-1 text-white">A+</div>
                      <div className="text-xs text-neutral-500 uppercase tracking-wider">UX Grade</div>
                    </div>
                  </div>

                  <div className="inline-flex items-center text-sm font-bold uppercase tracking-wider text-white group-hover:gap-4 transition-all gap-2">
                    View case study
                    <svg className="w-4 h-4 group-hover:translate-x-1 transition-transform text-accent" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 8l4 4m0 0l-4 4m4-4H3" />
                    </svg>
                  </div>
                </div>

                <div className="relative aspect-square md:aspect-auto bg-neutral-900 overflow-hidden">
                  <div className="absolute inset-0 bg-gradient-to-br from-accent/20 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-700 z-10"></div>
                  <img
                    src="/case-studies/smoove-move/logo.png"
                    alt="Smoove Move Logistics"
                    className="absolute inset-0 w-full h-full object-contain p-20 group-hover:scale-110 transition-transform duration-700 ease-out"
                  />
                </div>
              </div>
            </motion.div>
          </Link>
        </div>
      </section>

      {/* Capabilities Grid */}
      <section id="services" className="py-32 px-6 border-t border-white/5">
        <div className="max-w-7xl mx-auto">
          <motion.div
            className="mb-20"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, ease: [0.16, 1, 0.3, 1] }}
            viewport={{ once: true }}
          >
            <h2 className="text-5xl md:text-7xl lg:text-8xl font-display font-medium tracking-tighter text-white mb-6">
              What We Do
            </h2>
            <p className="text-xl text-neutral-400 font-light max-w-2xl">
              Business-focused creative services designed for impact.
            </p>
          </motion.div>

          <div className="grid md:grid-cols-3 gap-px bg-white/10 border border-white/10">
            {capabilities.map((capability, index) => (
              <div key={capability.title} className="bg-background">
                <motion.button
                  onClick={() => toggleSection(capability.title)}
                  className={`group relative w-full text-left transition-all duration-500 ${openSection === capability.title
                      ? 'bg-surfaceHighlight'
                      : 'bg-background hover:bg-surfaceHighlight/50'
                    }`}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.6, delay: index * 0.1, ease: [0.16, 1, 0.3, 1] }}
                  viewport={{ once: true }}
                >
                  <div className="p-10 md:p-12 h-full flex flex-col justify-between min-h-[400px]">
                    <div className="flex justify-between items-start w-full">
                      <span className="font-mono text-sm text-neutral-500">0{index + 1}</span>
                      <motion.div
                        animate={{ rotate: openSection === capability.title ? 45 : 0 }}
                        className="text-white"
                      >
                        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                          <path d="M12 5v14M5 12h14" />
                        </svg>
                      </motion.div>
                    </div>

                    <div>
                      <h3 className={`text-3xl md:text-4xl font-display font-medium mb-4 transition-colors ${openSection === capability.title ? 'text-accent' : 'text-white'
                        }`}>
                        {capability.title}
                      </h3>
                      <p className="font-light text-neutral-400 leading-relaxed mb-8">
                        {capability.description}
                      </p>

                      <div className={`inline-flex items-center text-sm font-bold uppercase tracking-wider gap-2 group-hover:gap-4 transition-all ${openSection === capability.title ? 'text-white' : 'text-neutral-500 group-hover:text-white'
                        }`}>
                        Explore
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 8l4 4m0 0l-4 4m4-4H3" />
                        </svg>
                      </div>
                    </div>
                  </div>
                </motion.button>

                {/* Expanded Content */}
                <AnimatePresence>
                  {openSection === capability.title && (
                    <motion.div
                      initial={{ opacity: 0, height: 0 }}
                      animate={{ opacity: 1, height: 'auto' }}
                      exit={{ opacity: 0, height: 0 }}
                      transition={{ duration: 0.4, ease: [0.16, 1, 0.3, 1] }}
                      className="overflow-hidden bg-surfaceHighlight border-t border-white/5"
                    >
                      <div className="p-10">
                        {/* Portfolio Images Grid */}
                        <div className="grid grid-cols-2 gap-4 mb-8">
                          {wrapProjects
                            .filter(project => project.category === capability.title)
                            .slice(0, 4)
                            .map((project) => (
                              project.images.slice(0, 1).map((image, imgIndex) => (
                                <motion.div
                                  key={`${project.id}-${imgIndex}`}
                                  className="aspect-square bg-neutral-900 overflow-hidden cursor-pointer group/image relative"
                                  whileHover={{ scale: 1.02 }}
                                  onClick={() => setSelectedImage(image)}
                                >
                                  <img
                                    src={image}
                                    alt={project.title}
                                    className="w-full h-full object-cover opacity-80 group-hover/image:opacity-100 transition-opacity duration-500"
                                  />
                                </motion.div>
                              ))
                            ))
                          }
                        </div>

                        <Link
                          to={`/portfolio#${capability.title.toLowerCase().replace(/\s+/g, '-')}`}
                          className="block w-full py-4 text-center border border-white/10 text-sm font-bold uppercase tracking-wider hover:bg-white hover:text-black transition-all duration-300"
                        >
                          View All Projects
                        </Link>
                      </div>
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>
            ))}
          </div>

          <motion.div
            className="text-center mt-20"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, ease: [0.16, 1, 0.3, 1] }}
            viewport={{ once: true }}
          >
            <a
              href="mailto:hello@chipahauk.com"
              className="inline-flex items-center text-sm text-neutral-500 hover:text-white transition-colors font-mono uppercase tracking-wider gap-2"
            >
              Partner with us
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M17 8l4 4m0 0l-4 4m4-4H3" />
              </svg>
            </a>
          </motion.div>
        </div>
      </section>

      {/* Image Lightbox */}
      <AnimatePresence>
        {selectedImage && (
          <motion.div
            className="fixed inset-0 z-50 flex items-center justify-center bg-black/95 backdrop-blur-xl p-6"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={() => setSelectedImage(null)}
          >
            <motion.img
              src={selectedImage}
              alt="Selected work"
              className="max-w-full max-h-full object-contain shadow-2xl"
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.9, opacity: 0 }}
              transition={{ type: "spring", damping: 25, stiffness: 300 }}
            />
            <button
              className="absolute top-6 right-6 text-white/50 hover:text-white transition-colors"
              onClick={() => setSelectedImage(null)}
            >
              <svg className="w-10 h-10" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </motion.div>
        )}
      </AnimatePresence>

      {/* CTA Section */}
      <section className="py-32 px-6 border-t border-white/5">
        <div className="max-w-4xl mx-auto text-center">
          <motion.h2
            className="text-5xl md:text-7xl font-display font-medium text-white mb-8 tracking-tighter"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, ease: [0.16, 1, 0.3, 1] }}
            viewport={{ once: true }}
          >
            Ready to accelerate?
          </motion.h2>

          <motion.p
            className="text-xl text-neutral-400 mb-12 font-light max-w-2xl mx-auto"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.1, ease: [0.16, 1, 0.3, 1] }}
            viewport={{ once: true }}
          >
            Partner with us to deliver exceptional creative solutions at unmatched speed.
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.2, ease: [0.16, 1, 0.3, 1] }}
            viewport={{ once: true }}
          >
            <a
              href="mailto:hello@chipahauk.com"
              className="inline-flex items-center px-10 py-5 bg-white text-black text-sm font-bold uppercase tracking-wider hover:bg-accent hover:text-white transition-all duration-300"
            >
              Start a conversation
            </a>
          </motion.div>
        </div>
      </section>
    </div>
  )
}

export default Home
